import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  advisorConsentsApi,
  advisorConsentsKeys,
  type PutAdvisorConsentsRequest,
} from '@client/api/waterlily';
import { termsOfServiceDef, smsConsentDef } from '@shared';
import { getUserIpAddress } from '@/util/getUserIpAddress';

interface UseAdvisorConsentsOptions {
  advisorId: string;
}

export function useAdvisorConsents({ advisorId }: UseAdvisorConsentsOptions) {
  const queryClient = useQueryClient();

  // Query to fetch existing consents
  const consentsQuery = useQuery({
    queryKey: advisorConsentsKeys.byAdvisor(advisorId),
    queryFn: () => advisorConsentsApi.getByAdvisorId(advisorId),
    enabled: !!advisorId,
  });

  // Check if the user has already agreed to the current terms of service
  const hasAgreedToTermsOfService = consentsQuery.data?.some(
    consent =>
      consent.consentVersionDescription === termsOfServiceDef.consentVersionDescription,
  ) ?? false;

  // Check if the user has already agreed to SMS consent
  const hasAgreedToSmsConsent = consentsQuery.data?.some(
    consent => consent.consentVersionDescription === smsConsentDef.consentVersionDescription,
  ) ?? false;

  // Mutation to save consents
  const saveConsentsMutation = useMutation({
    mutationFn: (data: PutAdvisorConsentsRequest) => advisorConsentsApi.saveConsents(data),
    onSuccess: () => {
      // Invalidate the consents query to refetch the updated data
      queryClient.invalidateQueries({ queryKey: advisorConsentsKeys.byAdvisor(advisorId) });
    },
  });

  // Helper function to save terms of service consent with metadata
  const saveTermsOfServiceConsent = useCallback(async () => {
    let ipAddress: string | null = null;
    try {
      ipAddress = await getUserIpAddress();
    } catch (error) {
      console.error('Failed to retrieve IP address:', error);
    }

    return saveConsentsMutation.mutateAsync({
      advisorId,
      consents: [
        {
          consentText: termsOfServiceDef.text,
          consentVersionDescription: termsOfServiceDef.consentVersionDescription,
          ipAddress,
          userAgent: navigator.userAgent,
        },
      ],
    });
  }, [advisorId, saveConsentsMutation]);

  // Helper function to save both required consents (TOS + SMS) with metadata
  const saveRequiredConsents = useCallback(async () => {
    let ipAddress: string | null = null;
    try {
      ipAddress = await getUserIpAddress();
    } catch (error) {
      console.error('Failed to retrieve IP address:', error);
    }

    const consentsToSave = [];

    // Only save TOS if not already saved
    if (!hasAgreedToTermsOfService) {
      consentsToSave.push({
        consentText: termsOfServiceDef.text,
        consentVersionDescription: termsOfServiceDef.consentVersionDescription,
        ipAddress,
        userAgent: navigator.userAgent,
      });
    }

    // Only save SMS if not already saved
    if (!hasAgreedToSmsConsent) {
      consentsToSave.push({
        consentText: smsConsentDef.text,
        consentVersionDescription: smsConsentDef.consentVersionDescription,
        ipAddress,
        userAgent: navigator.userAgent,
      });
    }

    // Only make API call if there are consents to save
    if (consentsToSave.length > 0) {
      return saveConsentsMutation.mutateAsync({
        advisorId,
        consents: consentsToSave,
      });
    }
  }, [advisorId, hasAgreedToTermsOfService, hasAgreedToSmsConsent, saveConsentsMutation]);

  return {
    // Query state
    consents: consentsQuery.data ?? [],
    isLoading: consentsQuery.isLoading,
    isFetching: consentsQuery.isFetching,
    isError: consentsQuery.isError,
    error: consentsQuery.error,

    // Derived state
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,

    // Mutation state
    saveConsents: saveConsentsMutation.mutate,
    saveConsentsAsync: saveConsentsMutation.mutateAsync,
    isSaving: saveConsentsMutation.isPending,
    saveError: saveConsentsMutation.error,

    // Helper for terms of service
    saveTermsOfServiceConsent,
    // Helper for both required consents
    saveRequiredConsents,
  };
}
